/**
 * Piezas del compositor de Sol para adjuntar imágenes: la fila de miniaturas
 * con su control para quitar, y el botón del clip con el selector de archivos.
 */
import { Paperclip, X } from 'lucide-react';
import { useRef } from 'react';

import { Button } from '@/components/ui/button';
import { TIPOS_DE_IMAGEN } from '../lib/adjuntos';
import type { ImagenAdjunta } from '../hooks/useImagenesAdjuntas';

export function AdjuntosDeImagen({
  imagenes,
  onQuitar,
  disabled,
}: {
  imagenes: ImagenAdjunta[];
  onQuitar: (id: string) => void;
  disabled?: boolean;
}) {
  if (imagenes.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5 px-1 pb-1.5" aria-label="Imágenes adjuntas">
      {imagenes.map((img, i) => (
        <li
          key={img.id}
          className="border-border-dim bg-surface-hi relative size-14 overflow-hidden rounded-md border"
        >
          <img
            src={img.vistaPrevia}
            alt={`Imagen adjunta ${i + 1}: ${img.nombre}`}
            className="size-full object-cover"
          />
          <Button
            type="button"
            variant="secondary"
            size="icon-xs"
            className="absolute right-0.5 top-0.5 size-5"
            aria-label={`Quitar imagen ${i + 1}`}
            title="Quitar imagen"
            disabled={disabled}
            onClick={() => onQuitar(img.id)}
          >
            <X />
          </Button>
        </li>
      ))}
    </ul>
  );
}

export function BotonAdjuntarImagen({
  onArchivos,
  disabled,
}: {
  onArchivos: (archivos: File[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Adjuntar imagen"
        title="Adjuntar imagen"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Paperclip />
      </Button>
      {/* input nativo oculto: el selector de archivos del sistema solo se abre desde un <input type="file">, y el botón de arriba es el que lo dispara. */}
      <input
        ref={inputRef}
        type="file"
        accept={TIPOS_DE_IMAGEN.join(',')}
        multiple
        hidden
        data-testid="sol-input-imagenes"
        onChange={(e) => {
          onArchivos(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
    </>
  );
}
